import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as y from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSimuladorEnergiaSolarState } from './state';
import { maskAccount, maskAgency, maskDate, unmaskMoney } from '../shared/utils/masks';
import { useScroll } from '../../../utils/useLayout';
import { useUpdateSimulation } from '../api/use-update-simulation';
import { getErrorData } from '../shared/utils/error';
import { validateAccount, validateAgency, validateDatePeriod } from '../shared/utils/validation';
import { createDatePeriodSchema, createMoneySchema } from '../shared/utils/schemas';
import { SIMULATION_STEPS } from './simulation-steps';
import { useValidateBankAccount } from '../api/use-validate-bank-account';
import { useUpdateInstallments } from '../api/use-update-installments';
import { devLog } from '../../../utils/browser';
import { MoneyTextFieldEmptyValue } from '../widgets/TextField/TextField';
import { usePostFinish } from '../api/use-post-finish';

export function useFormProposal() {
  const [offer, setOffer] = useState();

  const { scrollToTop } = useScroll();
  const {
    offer: offerState,
    token,
    showErrorStep,
    setSimulationStep,
    setFormProposal,
    options,
    simulationStep,
    installmentCache,
    loading,
    EventHandler,
    updateOfferDetails,
  } = useSimuladorEnergiaSolarState();

  const updateSimulation = useUpdateSimulation({ token });
  const validateBankAcc = useValidateBankAccount({ token });
  const updateInstalments = useUpdateInstallments({ token });
  const finishMutation = usePostFinish({ token });

  const isEnabled = simulationStep?.id === SIMULATION_STEPS.FORM_PROPOSAL.id;

  const validationSchema = useMemo(() => {
    let base = {
      firstDueDate: y
        .string()
        .required('Campo obrigatório')
        .concat(
          createDatePeriodSchema({
            minDate: offer?.minFirstDueDate,
            maxDate: offer?.maxFirstDueDate,
          }),
        ),
      institution: y.string().test('validate-institution', 'Campo obrigatório', (v, ctx) => {
        if (ctx.parent.paymentMethod !== 'D') return true;
        return !!v;
      }),
      accountType: y.string().test('validate-account-type', 'Campo obrigatório', (v, ctx) => {
        if (ctx.parent.paymentMethod !== 'D') return true;
        return !!v;
      }),
      agency: y.string().test('validate-agency', 'Número de agência inválido', (v, ctx) => {
        if (ctx.parent.paymentMethod !== 'D') return true;
        return validateAgency(v);
      }),
      account: y.string().test('validate-account', 'Número da conta inválido', (v, ctx) => {
        if (ctx.parent.paymentMethod !== 'D') return true;
        return validateAccount(v);
      }),
      entryValue: createMoneySchema({
        min: 200_00,
        max: 1_000_000_00,
        required: false,
        allowZeros: true,
      }),
    };

    const schema = y.object().shape(base);

    return yupResolver(schema);
  }, [offer?.maxFirstDueDate, offer?.minFirstDueDate]);

  const InitialValues = useMemo(() => {
    return {
      entryValue: MoneyTextFieldEmptyValue,
      installmentQuantity: 0,
      firstDueDate: '',
      paymentMethod: '',
      insurance: '',
      institution: options?.defaultBank || '',
      accountType: options?.defaultAccountType || '',
      account: '',
      agency: '',
    };
  }, [options?.defaultAccountType, options?.defaultBank]);

  const form = useForm({
    resolver: validationSchema,
    values: InitialValues,
    shouldUseNativeValidation: false,
    criteriaMode: 'firstError',
    mode: 'onBlur',
    disabled: !isEnabled,
  });

  const [currentPaymentMethod, currentInstallmentQuantity, currentInsurance] = form.watch([
    'paymentMethod',
    'installmentQuantity',
    'insurance',
  ]);

  const fields = useMemo(() => {
    const installmentOptions = (offer?.installmentOptions || [])
      .map((o) => ({
        ...o,
        disabled: !o.loaded,
      }))
      .filter((o) => !o.disabled);

    let fieldDefinition = {
      entryValue: {
        label: 'Entrada',
        type: 'money',
        inputMode: 'numeric',
        variant: 'text',
        maskFunction: (value = '', prev = '') => (`${value}`.length > 12 ? prev : value),
      },
      installmentQuantity: {
        label: 'Parcelas',
        type: 'select',
        options: installmentOptions,
      },
      firstDueDate: {
        label: 'Primeiro vencimento',
        inputMode: 'numeric',
        maskFunction: maskDate,
      },
    };

    let debitFieldDefinition = {
      institution: {
        label: 'Instituição',
        type: 'select',
        options: options?.banks || [],
        disabled: offer?.isAccountHolder,
      },
      accountType: {
        label: 'Tipo de conta',
        type: 'select',
        options: options?.accountTypes || [],
        disabled: offer?.isAccountHolder,
      },
      agency: {
        label: 'Agência',
        inputMode: 'numeric',
        maskFunction: maskAgency,
      },
      account: {
        label: 'Número da conta',
        inputMode: 'numeric',
        maskFunction: maskAccount,
      },
    };

    const toArray = (obj = {}) => Object.entries(obj).map(([name, value]) => ({ ...value, name }));

    const paymentFields = currentPaymentMethod === 'B' ? [] : toArray(debitFieldDefinition);

    return {
      fieldDefinition,
      paymentFields,
    };
  }, [
    currentPaymentMethod,
    offer?.installmentOptions,
    offer?.isAccountHolder,
    options?.accountTypes,
    options?.banks,
  ]);

  const updateEntryValue = async (value = '') => {
    if (updateSimulation.isPending || !offer || !isEnabled) {
      return;
    }

    if (unmaskMoney(value) === unmaskMoney(offer?.entryValueRecommended)) {
      return;
    }

    try {
      await updateSimulation.mutateAsync({
        entryValue: unmaskMoney(value) / 100,
      });

      EventHandler.emit('refetch-best-offer');
    } catch (error) {
      showErrorStep({
        ...getErrorData(error),
        title: 'Não foi possível atualizar o valor de entrada.',
        ctx: 'api-update-simulation-entry-value',
      });
    }
  };

  const updateFirstDueDate = async (value = '') => {
    if (updateSimulation.isPending || !offer || !value || !isEnabled) {
      return;
    }

    if (value === offer?.firstDueDate) {
      return;
    }

    const unmaskedDate = value.split('/').reverse().join('-');
    if (
      !validateDatePeriod({
        date: unmaskedDate,
        minDate: offer?.minFirstDueDate,
        maxDate: offer?.maxFirstDueDate,
      })
    ) {
      return;
    }

    try {
      await updateSimulation.mutateAsync({
        firstDueDate: unmaskedDate,
        installments: offer?.installmentQuantity || offerState?.installmentQuantity,
      });

      EventHandler.emit('refetch-best-offer');
    } catch (error) {
      showErrorStep({
        ...getErrorData(error),
        title: 'Não foi possível atualizar o primeiro vencimento.',
        ctx: 'api-update-simulation-first-due-date',
      });
    }
  };

  const updatePaymentMethod = async (value = '') => {
    if (!value || !offer || updateSimulation.isPending || !isEnabled) return;
    if (value === offer.paymentMethod) return;

    try {
      await updateSimulation.mutateAsync({
        paymentFormType: value,
      });

      form.setValue('paymentMethod', value, true);
      if (offer.paymentMethod === 'D' && value === 'B') {
        form.setValue('account', '', true);
        form.setValue('agency', '', true);
      }
      form.setValue('institution', options?.defaultBank || '', true);
      form.setValue('accountType', options?.defaultAccountType || '', true);

      EventHandler.emit('refetch-best-offer');
    } catch (error) {
      showErrorStep({
        ...getErrorData(error),
        title: 'Não foi possível atualizar a forma de pagamento.',
        ctx: 'api-update-simulation-payment-method',
      });
    }
  };

  const updateInstallmentQuantity = async (amount) => {
    if (!isEnabled || loading) return;

    try {
      await updateInstalments.mutateAsync({
        installments: Number(amount),
      });

      EventHandler.emit('refetch-best-offer');
    } catch (error) {
      showErrorStep({
        ...getErrorData(error),
        title: 'Não foi possível atualizar a quantidade de parcelas.',
        ctx: 'api-update-installments',
      });
    }
  };

  /** @type {import('react-hook-form').SubmitHandler<typeof InitialValues>} */
  const handleSubmit = async (values) => {
    if (!offer || updateSimulation.isPending) return;

    if (values.paymentMethod === 'D') {
      try {
        await validateBankAcc.mutateAsync({
          accountNumber: values.account,
          accountType: values.accountType,
          bankCode: values.institution,
          branchCode: values.agency,
        });
      } catch (error) {
        showErrorStep({
          ...getErrorData(error),
          title: 'Erro ao validar os dados da conta bancária.',
          scrollTo: '.proposal .tips',
          ctx: 'api-validate-bank-account',
        });
        return;
      }
    }

    try {
      await updateInstallmentQuantity(values.installmentQuantity);
    } catch (error) {
      showErrorStep({
        ...getErrorData(error),
        title: 'Não foi possível atualizar a quantidade de parcelas.',
        ctx: 'api-update-installments',
      });
      return;
    }

    try {
      const details = await finishMutation.mutateAsync({
        accountNumber: values.account,
        accountType: values.accountType,
        bankCode: values.institution,
        branchCode: values.agency,
      });

      updateOfferDetails(details);
    } catch (error) {
      showErrorStep({
        ...getErrorData(error),
        ctx: 'api-finish-simulation',
      });
      return;
    }

    setFormProposal(values);
    setSimulationStep(SIMULATION_STEPS.END_CONFIRMATION);
    scrollToTop();
  };

  useEffect(() => {
    if (!isEnabled || loading) return;

    if (installmentCache.installments) {
      if (!!currentInstallmentQuantity) {
        if (currentInstallmentQuantity === offerState?.installmentQuantity) {
          devLog(`\n[setOffer] CONTEXT ${offerState?.hash} \n`);
          setOffer(offerState);
          return;
        }

        const cached = installmentCache.installments?.find(
          (i) =>
            i.installmentQuantity === currentInstallmentQuantity && i.hash === offerState?.hash,
        );

        if (cached) {
          devLog(`\n[setOffer] +CACHE ${cached.hash} \n`);
          setOffer({
            ...offerState,
            ...cached.data,
          });
          return;
        }

        devLog('\n[setOffer] -MISS\n');
      }
    }

    return setOffer(offerState);
  }, [currentInstallmentQuantity, installmentCache.installments, isEnabled, loading, offerState]);

  useEffect(() => {
    const unsub = EventHandler.on('offer-updated', (v) => {
      if (
        offer?.installmentQuantity === v?.installmentQuantity &&
        offer?.installmentValue === v?.installmentValue &&
        offer?.hash === v?.hash
      ) {
        return;
      }

      devLog(`[Offer updated Event]
          q: ${offer?.installmentQuantity} -> ${v?.installmentQuantity}
          v: ${offer?.installmentValue} -> ${v?.installmentValue}
          h: ${offer?.hash} -> ${v?.hash}`);

      setOffer(v);
    });

    return () => {
      unsub.off('offer-updated');
    };
  }, [EventHandler, offer?.hash, offer?.installmentQuantity, offer?.installmentValue]);

  return {
    form,
    fields,
    handleSubmit: form.handleSubmit(handleSubmit),
    currentPaymentMethod,
    currentInstallmentQuantity,
    currentInsurance,
    loading: updateSimulation.isPending || validateBankAcc.isPending,
    updateEntryValue,
    updateFirstDueDate,
    updatePaymentMethod,
    offer,
  };
}
